import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { MessageStatus } from "@common/models/message-log";
import { Search, X } from "lucide-react";
import { useState } from "react";

interface MessageLogFiltersProps {
    searchQuery: string;
    statusFilter: MessageStatus | 'all';
    attemptTypeFilter: 'initial' | 'client-retry' | 'scheduler-retry' | 'all';
    whatsappStatusFilter: string | 'all';
    onSearchChange: (query: string) => void;
    onStatusFilterChange: (status: MessageStatus | 'all') => void;
    onAttemptTypeFilterChange: (type: 'initial' | 'client-retry' | 'scheduler-retry' | 'all') => void;
    onWhatsappStatusFilterChange: (status: string | 'all') => void;
    onClearFilters: () => void;
}

export function MessageLogFilters({
    searchQuery,
    statusFilter,
    attemptTypeFilter,
    whatsappStatusFilter,
    onSearchChange,
    onStatusFilterChange,
    onAttemptTypeFilterChange,
    onWhatsappStatusFilterChange,
    onClearFilters,
}: MessageLogFiltersProps) {
    const hasActiveFilters = 
        searchQuery !== '' || 
        statusFilter !== 'all' || 
        attemptTypeFilter !== 'all' || 
        whatsappStatusFilter !== 'all';

    return (
        <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
                <label className="text-sm font-medium mb-1.5 block">Search</label>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                        placeholder="Search by message, contact, or ID..."
                        value={searchQuery}
                        onChange={(e) => onSearchChange(e.target.value)}
                        className="pl-9"
                    />
                </div>
            </div>

            <div className="w-[180px]">
                <label className="text-sm font-medium mb-1.5 block">Status</label>
                <Select value={statusFilter} onValueChange={onStatusFilterChange}>
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="before-wa-send">Before Send</SelectItem>
                        <SelectItem value="bt-sent">Sending</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="received">Received</SelectItem>
                        <SelectItem value="read">Read</SelectItem>
                        <SelectItem value="played">Played</SelectItem>
                        <SelectItem value="failed">Failed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="w-[180px]">
                <label className="text-sm font-medium mb-1.5 block">Attempt Type</label>
                <Select value={attemptTypeFilter} onValueChange={onAttemptTypeFilterChange}>
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="initial">Initial</SelectItem>
                        <SelectItem value="client-retry">Client Retry</SelectItem>
                        <SelectItem value="scheduler-retry">Scheduler Retry</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="w-[180px]">
                <label className="text-sm font-medium mb-1.5 block">WhatsApp Status</label>
                <Select value={whatsappStatusFilter} onValueChange={onWhatsappStatusFilterChange}>
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="connected">Connected</SelectItem>
                        <SelectItem value="disconnected">Disconnected</SelectItem>
                        <SelectItem value="qrcode">QR Code</SelectItem>
                        <SelectItem value="unknown">Unknown</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {hasActiveFilters && (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={onClearFilters}
                    className="mb-0"
                >
                    <X className="h-4 w-4 mr-1" />
                    Clear Filters
                </Button>
            )}
        </div>
    );
}

